import { Form } from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	CalendarAppDetailsTileModel,
	CalendarUpdateTileModel,
	updateCalendarTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
} from "client/Validation";
import {
	isNotNullNorUndefined,
	isNotEmpty,
	doesNotExceedLength
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState,
} from "react";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";

type CalendarEditFormData = EditTileFromData<CalendarUpdateTileModel>;

const validationBuilder: IValidationConfiguration<CalendarEditFormData> =
	createValidationConfiguration<CalendarEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

export type EditCalendarTileProps = {
	initialModel: CalendarAppDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditCalendarTile = forwardRef(
	(props: EditCalendarTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<CalendarEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
		});

		const formHandler: IFormHandler<CalendarEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { updateHandler } = useUpdateTile<CalendarUpdateTileModel>(
			formHandler,
			updateCalendarTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(
			ref,
			() => ({
				updateHandler
			}),
			[updateHandler]
		);

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</Form>
		);
	}
);

EditCalendarTile.displayName = "EditCalendarTile";
